import React, { useState } from 'react';
import { Check, X, Edit2, AlertCircle, Trash2, List } from 'lucide-react';

const ReviewCard = ({ interrupt, onDecision, onEditRequest }) => {
    if (!interrupt) return null;

    // In our implementation, interrupt is usually a list containing one or more review objects
    // If it's the raw data from data-interrupt, it might be nested
    const review = Array.isArray(interrupt) ? interrupt[0] : interrupt;

    if (!review) return null;

    const { type, title, summary, action_args } = review;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [decision, setDecision] = useState(null); // null, 'approve', 'reject'

    const handleApprove = async () => {
        setIsSubmitting(true);
        try {
            await onDecision({
                type: 'approve',
                args: action_args
            });
            setDecision('approve');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReject = async () => {
        setIsSubmitting(true);
        try {
            await onDecision({
                type: 'reject'
            });
            setDecision('reject');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = () => {
        if (onEditRequest) {
            // Updated to be more user-friendly, excluding the raw JSON string
            onEditRequest(`I want to edit the "${title || 'requested action'}" proposal. Specifically...`);
        }
    };

    const getIcon = () => {
        if (decision === 'approve') return <Check size={18} className="text-white" />;
        if (decision === 'reject') return <X size={18} className="text-white" />;
        if (type?.includes('watchlist')) return <List size={18} className="text-blue-500" />;
        if (type?.includes('alert')) return <AlertCircle size={18} className="text-orange-500" />;
        if (type?.includes('delete')) return <Trash2 size={18} className="text-red-500" />;
        return <Check size={18} className="text-green-500" />;
    };

    const getBgColor = () => {
        if (decision === 'approve') return 'bg-green-500 border-green-600';
        if (decision === 'reject') return 'bg-gray-500 border-gray-600';
        if (type?.includes('delete')) return 'bg-red-50 border-red-100';
        if (type?.includes('watchlist')) return 'bg-blue-50 border-blue-100';
        if (type?.includes('alert')) return 'bg-orange-50 border-orange-100';
        return 'bg-green-50 border-green-100';
    };

    // Compact Decision State UI
    if (decision) {
        return (
            <div className={`mt-4 overflow-hidden rounded-xl border ${getBgColor()} shadow-sm animate-in zoom-in-95 duration-300`}>
                <div className="px-4 py-2.5 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center shadow-inner">
                            {getIcon()}
                        </div>
                        <div className="flex flex-col">
                            <span className="text-[11px] font-bold text-white/80 uppercase tracking-tight leading-none">
                                {decision === 'approve' ? 'Action Executed' : 'Action Cancelled'}
                            </span>
                            <span className="text-sm font-bold text-white leading-tight">
                                {title || 'Requested Action'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`mt-4 overflow-hidden rounded-xl border ${getBgColor()} shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300`}>
            {/* Header */}
            <div className="px-4 py-3 border-b border-inherit flex items-center justify-between">
                <div className="flex items-center gap-2">
                    {getIcon()}
                    <span className="text-sm font-bold text-gray-800">{title || 'Review Action'}</span>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 bg-white/50 px-2 py-0.5 rounded-full border border-inherit">
                    Requires Approval
                </span>
            </div>

            {/* Content */}
            <div className="p-4">
                <div className="space-y-2">
                    {Array.isArray(summary) ? summary.map((line, i) => (
                        <p key={i} className="text-xs text-gray-600 leading-relaxed font-medium">
                            {line}
                        </p>
                    )) : (
                        <p className="text-xs text-gray-600 leading-relaxed font-medium">{summary}</p>
                    )}
                </div>
            </div>

            {/* Actions */}
            <div className="px-4 py-3 bg-white/40 border-t border-inherit flex items-center justify-between gap-3">
                <button
                    onClick={handleReject}
                    disabled={isSubmitting}
                    className="flex-1 px-3 py-2 text-xs font-bold text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all flex items-center justify-center gap-1.5"
                >
                    <X size={14} strokeWidth={3} />
                    Reject
                </button>

                <button
                    onClick={handleEdit}
                    disabled={isSubmitting}
                    className="px-3 py-2 text-xs font-bold text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all flex items-center justify-center gap-1.5"
                >
                    <Edit2 size={14} strokeWidth={3} />
                    Edit
                </button>

                <button
                    onClick={handleApprove}
                    disabled={isSubmitting}
                    className="flex-[1.5] px-4 py-2 bg-black text-white text-xs font-bold rounded-lg hover:bg-gray-800 transition-all shadow-sm active:scale-95 flex items-center justify-center gap-1.5 disabled:opacity-50"
                >
                    {isSubmitting ? (
                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                        <Check size={14} strokeWidth={3} />
                    )}
                    Confirm & Execute
                </button>
            </div>
        </div>
    );
};

export default ReviewCard;
